import React, { FC, useEffect, useState } from "react";
import "./index.scss";
import { Carousel } from "../Carousel/Carousel";
import { getMediaDevice, isSpecialEvent, Arrow } from "../../helpers";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";
import { getLatestScores } from "../../services/tournaments";
import { Match } from "../OrderOfPlay/src/types";

export interface ITournament {
  tournamentId: number;
  matchTypeCode: string;
  matchRound: number;
  circuitCode: string;
}

interface Props {
  tournaments: ITournament[];
}

interface MathLatestScore extends Match {
  discipline: string;
  roundNumber: string;
}

const MatchesList: FC<{
  page: number;
  itemsToDisplay: Record<string, number>;
  matches: Partial<MathLatestScore>[];
}> = ({ page, itemsToDisplay, matches }) => {
  return (
    <ReactCSSTransitionGroup
      transitionName="carousel__animation"
      transitionEnterTimeout={500}
      transitionLeaveTimeout={500}
      className={`carousel__animation-container`}
    >
      <div className="latest-scores__list">
        {matches
          .slice(
            page * itemsToDisplay[getMediaDevice()],
            (page + 1) * itemsToDisplay[getMediaDevice()]
          )
          .map((match: Partial<MathLatestScore>) => (
            <Match {...match} key={match.matchId} />
          ))}
      </div>
    </ReactCSSTransitionGroup>
  );
};

const Match: FC<Partial<MathLatestScore>> = ({
  teams,
  discipline,
  roundNumber
}) => {
  return (
    <div className="latest-scores__card">
      <div className="latest-scores__card--info">
        <div>{discipline}</div>
        <div>{roundNumber}</div>
      </div>
      {teams && (
        <div className="latest-scores__card--details">
          {teams.map(
            ({ players, isWinner, seeding, scores }, teamKey: number) => (
              <div
                key={`matchId-${teamKey}`}
                className={`latest-scores__card--player-${
                  !teamKey ? "top" : "bottom"
                }`}
              >
                <div
                  className={`latest-scores__card--block-${
                    players.filter(player => player).length > 1
                      ? "double"
                      : "single"
                  }`}
                >
                  {players.filter(item => item).length > 0 ? (
                    players.map((player, playerKey) =>
                      player ? (
                        <a key={player.playerId} href={player.profileLink}>
                          <div className="latest-scores__card--flag">
                            <div
                              className={`itf-flags itf-flags--${player.nationality}`}
                            ></div>
                            <span>{player.nationality}</span>
                          </div>
                          <span>
                            {player.givenName.charAt(0)}. {player.familyName}{" "}
                            {playerKey !== 0 && seeding ? `[${seeding}]` : null}
                          </span>
                        </a>
                      ) : null
                    )
                  ) : (
                    <div className="latest-scores__card--block-text">BYE</div>
                  )}
                </div>
                {isWinner ? (
                  <div className="latest-scores__card--winner" />
                ) : null}
                <div className="latest-scores__card--score">
                  {scores.map((score, scoreKey) =>
                    score !== null ? (
                      <span key={scoreKey}>
                        {score.score}
                        <sup>{score.losingScore}</sup>
                      </span>
                    ) : null
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const LatestScores: FC<Props> = ({ tournaments }) => {
  const itemsToDisplay = { DESKTOPXL: 4, DESKTOP: 3, TABLET: 2, MOBILE: 1 };
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<Partial<MathLatestScore>[]>([]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data: Partial<MathLatestScore>[] = await getLatestScores(
          tournaments
        );
        setResults(data);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const isOlympics =
    location.pathname.indexOf("lympi") >= 0 ||
    location.pathname.indexOf("limpi") >= 0;

  return !isLoading && results.length ? (
    <div
      className={`latest-scores ${
        isOlympics || isSpecialEvent ? "olympics" : ""
      }`}
    >
      <div className="page-section__inner">
        <Carousel
          totalItems={results.length}
          itemsToDisplay={itemsToDisplay}
          pagerPerItem
          chevronPrev={isSpecialEvent ? <Arrow fill="#fff" /> : <></>}
          chevronNext={isSpecialEvent ? <Arrow fill="#fff" /> : <></>}
        >
          <MatchesList
            page={0}
            matches={results}
            itemsToDisplay={itemsToDisplay}
          />
        </Carousel>
      </div>
    </div>
  ) : null;
};

export default LatestScores;
